import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { useDownloadDocument } from '@/hooks/useDownloadDocument';
import { useGetDocumentInfo } from '@/hooks/useGetDocumentInfo';
import { cx, downloadFileFromFileInfo } from '@/utils';
import { ContentSearchType, DocumentEntity } from '@common/Providers/GraphqlProvider/generated';
import { formatDateWithUTC } from '@utils/DayjsFormater';
import { showErrorToast, showSuccessToast } from '@utils/toast/toastHelpers';
import dayjs from 'dayjs';
import { AlertCircle, Copy, Download } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/common/Dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';

// Configure PDF worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface DocumentPreviewModalProps {
  documentId: string | null;
  docTitle?: string;
  documentType?: ContentSearchType;
  onClose: () => void;
}

const DocumentPreviewModal = ({
  documentId,
  docTitle,
  documentType = ContentSearchType.Document,
  onClose,
}: DocumentPreviewModalProps) => {
  const [pdfLoading, setPdfLoading] = useState(true);
  const [copying, setCopying] = useState(false);

  const { download, downloading } = useDownloadDocument();
  const { getDocumentInfo, documentData: documentSelected, loading, error } = useGetDocumentInfo();

  // Auto-load document when documentId changes
  useEffect(() => {
    if (documentId) {
      loadDocument();
    }
  }, [documentId]);

  const loadDocument = async () => {
    if (!documentId) return;

    try {
      await getDocumentInfo(documentId, documentType);
    } catch (err) {
      console.error(`Error al obtener ${documentType} para previsualizar`, err);
    }
  };

  const handleDownload = async () => {
    if (documentId) {
      await download(documentId, documentType);
    }
  };

  const handleCopyLink = async () => {
    try {
      setCopying(true);
      const documentURL = documentSelected?.file?.downloadUrl || documentSelected?.file?.url;
      if (documentURL) {
        await navigator.clipboard.writeText(documentURL);
        showSuccessToast('Link copiado al portapapeles');
      } else {
        showErrorToast('No se encontró URL del archivo');
      }
    } catch (error) {
      console.error('Error al copiar el link:', error);
      showErrorToast('Error al copiar el link');
    } finally {
      setCopying(false);
    }
  };

  const getFileType = () => {
    if (!documentSelected?.file?.fileExtension) return 'unknown';
    const ext = documentSelected.file.fileExtension.toLowerCase();
    if (ext === 'pdf') return 'pdf';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'image';
    if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext)) return 'office';
    if (['txt', 'json', 'xml', 'html', 'css', 'js'].includes(ext)) return 'text';
    return 'other';
  };

  const renderPreviewContent = () => {
    if (loading) {
      return (
        <div className="flex h-full items-center justify-center">
          <div className="text-center">
            <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
            <p>Cargando documento...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex h-full items-center justify-center">
          <div className="text-center">
            <AlertCircle className="mx-auto mb-2 h-12 w-12 text-red-500" />
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      );
    }

    if (!documentSelected) {
      return (
        <div className="flex h-full items-center justify-center">
          <div className="text-center">
            <AlertCircle className="mx-auto mb-2 h-12 w-12 text-gray-400" />
            <p>No se encontró el documento</p>
          </div>
        </div>
      );
    }

    const fileType = getFileType();
    const fileUrl = documentSelected?.file?.downloadUrl || documentSelected?.file?.url;

    if (!fileUrl) {
      return (
        <div className="flex h-full items-center justify-center">
          <div className="text-center">
            <AlertCircle className="mx-auto mb-2 h-12 w-12 text-red-500" />
            <p>No se encontró la URL del archivo</p>
          </div>
        </div>
      );
    }

    switch (fileType) {
      case 'pdf':
        return (
          <div className="relative h-full w-full">
            {pdfLoading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-sm">
                <div className="flex flex-col items-center">
                  <div className="mb-3 h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  <p className="text-sm text-primary">Cargando documento...</p>
                </div>
              </div>
            )}
            <embed
              src={`${fileUrl}`}
              className="h-full w-full border-0"
              onLoad={() => setPdfLoading(false)}
              title={`Vista previa de ${documentSelected?.file?.fileName || docTitle}`}
            />
          </div>
        );

      case 'image':
        return (
          <div className="flex h-full items-center justify-center p-4">
            <Image
              src={fileUrl}
              alt={documentSelected?.file?.fileName || docTitle || 'Imagen'}
              width={800}
              height={600}
              className="max-h-full max-w-full object-contain"
            />
          </div>
        );

      case 'office': {
        // Ensure URL is absolute for the viewer
        const absoluteUrl = fileUrl.startsWith('http')
          ? fileUrl
          : `${window.location.origin}${fileUrl}`;
        const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
          absoluteUrl
        )}`;

        return (
          <div className="flex h-full w-full flex-col">
            <iframe
              src={officeViewerUrl}
              className="h-full w-full flex-1 border-0"
              title={`Vista previa de ${documentSelected?.file?.fileName || docTitle}`}
              onError={(e) => console.error('Error loading Office Viewer', e)}
            />
            <div className="bg-gray-50 py-2 text-center text-xs text-gray-500">
              Si el documento no carga, puedes{' '}
              <button
                onClick={handleDownload}
                className="font-medium text-primary underline hover:text-primaryDark"
              >
                descargarlo aquí
              </button>
            </div>
          </div>
        );
      }

      case 'text':
        return (
          <div className="h-full">
            <iframe
              src={fileUrl}
              className="h-full w-full border-0"
              title={`Vista previa de ${documentSelected?.file?.fileName || docTitle}`}
            />
          </div>
        );

      default:
        return (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <AlertCircle className="text-yellow-500 mx-auto mb-2 h-12 w-12" />
              <p className="mb-2">Vista previa no disponible para este tipo de archivo</p>
              <p className="mb-4 text-sm text-gray-600">
                Tipo: {documentSelected?.file?.fileExtension?.toUpperCase() || 'Desconocido'}
              </p>
              <button
                onClick={() => window.open(fileUrl, '_blank')}
                className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
              >
                Abrir en nueva ventana
              </button>
            </div>
          </div>
        );
    }
  };

  // Refs y estados para el tamaño del contenedor PDF
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setContainerWidth(width);
      setContainerHeight(height);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const fieldsContent = (
    <div className="mt-4">
      <CustomAccordion title="Campos básicos del documento" defaultOpen={true}>
        <div className="space-y-4 text-sm">
          <div>
            <p className="font-workSans text-[14px] font-semibold text-tundora">
              Dependencia asociada
            </p>
            <p className="font-workSans text-[14px] font-normal text-dimGray">
              {documentSelected?.dependency?.name}
            </p>
          </div>
          <div>
            <p className="font-workSans text-[14px] font-semibold text-tundora">
              Descripción del documento
            </p>
            <p className="truncate font-workSans text-[14px] font-normal text-dimGray">
              {documentSelected?.description}
            </p>
          </div>
          <div>
            <p className="font-workSans text-[14px] font-semibold text-tundora">
              Fecha de expedición
            </p>
            <p className="font-workSans text-[14px] font-normal text-dimGray">
              {formatDateWithUTC(documentSelected?.createdAt)}
            </p>
          </div>
          <div>
            <p className="font-workSans text-[14px] font-semibold text-tundora">
              Fecha de publicación
            </p>
            <p className="font-workSans text-[14px] font-normal text-dimGray">
              {formatDateWithUTC(documentSelected?.publishDate)}
            </p>
          </div>
        </div>
      </CustomAccordion>

      <CustomAccordion title="Campos adicionales" defaultOpen={true}>
        <div className="space-y-4 text-sm">
          {documentSelected?.metadata
            ?.filter((meta: any) => {
              if (!meta.value) return false;
              // Filtrar metadatos cuyo valor sea exactamente igual al título o la descripción del documento
              // para evitar mostrar información duplicada que ya está en los "Campos básicos"
              if (
                meta.value === documentSelected.title ||
                meta.value === documentSelected.description
              )
                return false;
              return true;
            })
            .map((meta: any) => (
              <div key={meta.id || meta.schemaField?.id}>
                <p className="font-workSans text-[14px] font-semibold text-tundora">
                  {meta.schemaField?.fieldName || 'Campo desconocido'}
                </p>
                <p className="break-words font-workSans text-[14px] font-normal text-dimGray">
                  {meta.value}
                </p>
              </div>
            ))}
          {(!documentSelected?.metadata ||
            documentSelected.metadata.filter((meta: any) => {
              if (!meta.value) return false;
              if (
                meta.value === documentSelected.title ||
                meta.value === documentSelected.description
              )
                return false;
              return true;
            }).length === 0) && (
            <p className="font-workSans text-[14px] font-normal text-dimGray">
              No hay campos adicionales definidos.
            </p>
          )}
        </div>
      </CustomAccordion>
    </div>
  );

  return (
    <Dialog
      open={!!documentId}
      onOpenChange={() => {
        onClose();
        setPdfLoading(true);
      }}
    >
      <DialogContent className="top-4 flex h-[calc(100dvh-2rem)] w-[95vw] max-w-7xl flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="mt-2 flex flex-col items-start gap-2 md:flex-row md:items-center md:justify-between">
            <div className="flex w-full flex-col md:flex-1">
              <span className="block w-full max-w-full break-words font-workSans text-[18px] font-semibold leading-snug text-primary md:max-w-[700px] md:truncate md:text-[24px]">
                {documentSelected?.title || docTitle || 'Documento'}
              </span>
            </div>
            {documentSelected && (
              <div className="w-full md:w-96">
                <div className="flex flex-wrap items-center justify-start gap-2 md:justify-end">
                  <button
                    onClick={handleCopyLink}
                    disabled={copying}
                    className="flex h-[36px] items-center gap-2 rounded-[24px] px-3 font-workSans text-[16px] font-medium text-primary hover:bg-primaryDark hover:text-white"
                    title="Copiar link del documento"
                  >
                    <Copy size={16} />
                    {copying ? 'Copiando...' : 'Copiar link'}
                  </button>
                  <button
                    onClick={handleDownload}
                    disabled={downloading}
                    className="flex h-[36px] items-center gap-2 rounded-[24px] px-3 font-workSans text-[16px] font-medium text-primary hover:bg-primaryDark hover:text-white"
                    title="Descargar documento"
                  >
                    <Download size={16} />
                    {downloading ? 'Descargando...' : 'Descargar'}
                  </button>
                </div>
              </div>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
          {/* Desktop/tablet: split view */}
          <div className="hidden min-h-0 w-full flex-1 overflow-hidden md:flex">
            <div className="flex-1 overflow-auto bg-white px-2 py-4">
              <div className="flex h-full w-full justify-center">{renderPreviewContent()}</div>
            </div>

            <div className="h-full w-96 flex-shrink-0 overflow-y-auto px-4">{fieldsContent}</div>
          </div>

          {/* Mobile: tabs */}
          <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden md:hidden">
            <Tabs
              defaultValue="preview"
              className="flex min-h-0 w-full flex-1 flex-col overflow-hidden"
            >
              <TabsList className="mx-2 mt-2 grid w-[calc(100%-1rem)] grid-cols-2">
                <TabsTrigger value="preview" className="w-full">
                  Documento
                </TabsTrigger>
                <TabsTrigger value="fields" className="w-full">
                  Campos
                </TabsTrigger>
              </TabsList>

              <TabsContent value="preview" className="mt-0 min-h-0 flex-1 overflow-hidden">
                <div className="h-full overflow-auto bg-white px-2 py-4">
                  <div className="flex h-full w-full justify-center">{renderPreviewContent()}</div>
                </div>
              </TabsContent>

              <TabsContent value="fields" className="mt-0 min-h-0 flex-1 overflow-y-auto px-4 pb-4">
                {fieldsContent}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentPreviewModal;

const CustomAccordion = ({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mb-4 rounded-lg border border-gray-200 bg-white shadow-sm">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between rounded-t-lg bg-primaryLight px-4 py-3 text-left font-workSans font-semibold text-primary"
      >
        <span className={'font-workSans text-[14px] font-semibold text-primaryDark'}>{title}</span>
        <svg
          className={`h-5 w-5 transform transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && <div className="px-5 py-4">{children}</div>}
    </div>
  );
};
